import { useMemo } from 'react';
import clsx from 'clsx';
import dynamic from 'next/dynamic';

import { PlatformID, type Player, PlayerPosition, type Shirt as ShirtProps } from '@common/clients/api';
import { AddPlayerIcon } from '@common/svg/icons/components/AddPlayerIcon';
import { SwitchOutIcon } from '@common/svg/icons/components/SwitchOutIcon';
import { type MatchEvent } from '@common/types/MatchEvent';
import { Route } from '@common/types/Route';
import { useContextData } from '@common/useContextData';
import { InternalLink } from '@web/atoms/InternalLink';
import { type PlayerHighlights } from '@web/atoms/match/types';
import { getCardIcon, getGoalsIcon, getPlayerHighlights } from '@web/atoms/match/utils';
import styles from '@web/molecules/FootballFormation/FootballFormation.module.scss';
import { isPlaceholderPlayer } from '@web/templates/(football)/LineupGenerator/LineupGenerator.utils';

const Jersey = dynamic(() => import('@common/atoms/Jersey').then((module) => module.Jersey));

interface Props {
    player: Player;
    events: MatchEvent[];
    isLineUpGenerator?: boolean;
    isHomeTeam?: boolean;
    shirt?: ShirtProps;
    setSelectedPlayerID?: (_: string) => void;
}

export const PlayerElement = ({
    player,
    events,
    isLineUpGenerator = false,
    isHomeTeam = false,
    shirt,
    setSelectedPlayerID,
}: Props) => {
    const { platform } = useContextData();
    const { playerGoals, playerOwnGoals, isPlayerSubstitutedOut, playerCardSeverity }: PlayerHighlights =
        useMemo(() => getPlayerHighlights(events, player.id), [events, player.id]);

    const isPlaceholder = isPlaceholderPlayer(player.id);
    const isVI = platform.id === PlatformID.VI;
    const isAddMode = isLineUpGenerator && isPlaceholder;
    const isGoalkeeper = player.position === PlayerPosition.GOALKEEPER;

    const playerName = useMemo(() => {
        if (isAddMode) return 'Toevoegen';
        if (isVI && player.tag?.shortTitle) return player.tag.shortTitle;
        return player.name;
    }, [isAddMode, isVI, player]);

    const playerNameClass = clsx(
        isLineUpGenerator ? styles['highlighted-player-name'] : styles['player-name'],
        isAddMode && styles.clickable,
        isPlaceholder && styles['placeholder-spacing'],
    );

    const handlePlayerClick = () => {
        if (isAddMode && setSelectedPlayerID) {
            setSelectedPlayerID(player.id);
        }
    };

    const resolvedShirt = useMemo(() => {
        if (isGoalkeeper) {
            return {
                pattern: '212',
                primary: '#FA3A94',
                secondary: '#EA1870',
                ribbing: '#1A1617',
            };
        }

        if (shirt) return shirt;

        return {
            showDefaultJerseys: true,
            isHomeTeam,
        };
    }, [isGoalkeeper, shirt, isHomeTeam]);

    const playerContent = (
        <>
            <span className={styles['player-events-holder']}>
                <>
                    {isLineUpGenerator ? (
                        <span
                            className={clsx(
                                styles['shirt-icon'],
                                styles.clickable,
                                styles['is-lineup-generator'],
                            )}
                            onClick={() => {
                                if (setSelectedPlayerID) {
                                    setSelectedPlayerID(player.id);
                                }
                            }}
                        >
                            {isPlaceholderPlayer(player.id) ? (
                                <span className={styles['empty-player']}>
                                    <span className={styles['empty-player-icon']}>
                                        <AddPlayerIcon />
                                    </span>
                                </span>
                            ) : (
                                <Jersey {...resolvedShirt} />
                            )}
                        </span>
                    ) : (
                        <>
                            {platform.id === PlatformID.VI ? (
                                <span className={styles['shirt-icon']}>
                                    <Jersey {...resolvedShirt} />
                                </span>
                            ) : (
                                <span className={styles['shirt-number']}>{player.shirtNumber}</span>
                            )}
                        </>
                    )}

                    {playerCardSeverity || isPlayerSubstitutedOut || playerGoals || playerOwnGoals ? (
                        <span className={styles['events-holder']}>
                            <span className={styles['card-event']}>{getCardIcon(playerCardSeverity)}</span>
                            {isPlayerSubstitutedOut ? (
                                <span className={styles['substitution-event']}>
                                    <SwitchOutIcon />
                                </span>
                            ) : null}
                            <span className={styles['goal-event']}>
                                {getGoalsIcon(playerGoals.length, playerOwnGoals.length, platform.id)}
                            </span>
                        </span>
                    ) : null}
                </>
            </span>
            <p className={playerNameClass} onClick={handlePlayerClick}>
                {playerName}
            </p>
        </>
    );

    return (
        <span className={clsx(styles.player, isLineUpGenerator && styles['line-up-generator-player'])}>
            {!isLineUpGenerator && player.tag ? (
                <InternalLink route={Route.Player} params={{ tag: player.tag.tagSlug }}>
                    {playerContent}
                </InternalLink>
            ) : (
                playerContent
            )}
        </span>
    );
};
